import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, type Observable, shareReplay, throwError } from 'rxjs';

import { environment } from '@env';
import { DataFreshness } from '@core/data-source/data-freshness';
import {
  type ApiEnvelope,
  type BlockQuery,
  type BlockSummary,
  type DashboardFilterValues,
  type DashboardKey,
  type DashboardSummary,
  type DetailsPage,
  type FlashBootstrap,
  type KpiCard,
  type KpiTarget,
} from './analytics-api.types';

/** Мимо HTTP-кэша: после выгрузки витрины ответ по тому же адресу уже другой. */
const NO_STORE = { cache: 'no-store' } as const;

@Injectable({ providedIn: 'root' })
export class AnalyticsApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.analyticsApiUrl;
  private readonly registries = new Map<
    DashboardKey,
    Observable<ReadonlyMap<string, BlockSummary>>
  >();
  private dashboards$: Observable<readonly DashboardSummary[]> | null = null;

  /** Статус блока меняется, когда у бэка появляется источник, — запомненный состав устаревает. */
  private readonly freshness = new DataFreshness(() => {
    this.dashboards$ = null;
    this.registries.clear();
  });

  /** Один запрос на несколько минут: по нему и доступ к дашбордам, и состав их блоков. Ошибка не кэшируется. */
  dashboards(): Observable<readonly DashboardSummary[]> {
    this.freshness.refreshIfStale();
    this.dashboards$ ??= this.http
      .get<DashboardSummary[]>(`${this.base}/dashboards`, NO_STORE)
      .pipe(
        catchError((error: unknown) => {
          this.dashboards$ = null;

          return throwError(() => error);
        }),
        shareReplay(1),
      );

    return this.dashboards$;
  }

  /** Source calendar and linked catalogs already restricted to the caller's scope. */
  flashBootstrap(
    dashboard: 'hotels' | 'restaurants',
  ): Observable<ApiEnvelope<readonly FlashBootstrap[]>> {
    return this.http.get<ApiEnvelope<readonly FlashBootstrap[]>>(
      `${this.base}/dashboards/${dashboard}/bootstrap`,
      NO_STORE,
    );
  }

  kpiTargets(): Observable<readonly KpiTarget[]> {
    return this.http.get<KpiTarget[]>(`${this.base}/kpi-targets`, NO_STORE).pipe(
      map((rows) =>
        rows.map((row) => ({
          ...row,
          target: toNumber(row.target) ?? 0,
          yellow: toNumber(row.yellow) ?? 0,
          red: toNumber(row.red) ?? 0,
        })),
      ),
    );
  }

  block<T = readonly Readonly<Record<string, unknown>>[]>(
    dashboard: DashboardKey,
    block: string,
    query: BlockQuery,
  ): Observable<ApiEnvelope<T>> {
    return this.http.get<ApiEnvelope<T>>(`${this.base}/dashboards/${dashboard}/blocks/${block}`, {
      ...NO_STORE,
      params: toParams(query),
    });
  }

  kpiBlock(
    dashboard: DashboardKey,
    block: string,
    query: BlockQuery,
  ): Observable<ApiEnvelope<readonly KpiCard[]>> {
    return this.block<readonly KpiCard[]>(dashboard, block, query).pipe(
      map((envelope) => ({ ...envelope, data: (envelope.data ?? []).map(normalizeKpiCard) })),
    );
  }

  /** Окно сравнения раньше начала данных — карточка без тренда, а не ошибка. */
  kpiBlockWithTrend(
    dashboard: DashboardKey,
    block: string,
    query: BlockQuery,
  ): Observable<ApiEnvelope<readonly KpiCard[]>> {
    const withoutComparison: BlockQuery = { ...query, compare: 'none' };
    // Omit custom bounds entirely when retrying without a comparison.
    const { compare_date_from: _from, compare_date_to: _to, ...fallbackQuery } = withoutComparison;
    return this.kpiBlock(dashboard, block, query).pipe(
      catchError((error: unknown) =>
        isCalendarShift(error) && query.compare !== undefined && query.compare !== 'none'
          ? this.kpiBlock(dashboard, block, fallbackQuery).pipe(
              map((envelope) => ({
                ...envelope,
                data: envelope.data.map((card) => ({ ...card, trend_vs_previous: null })),
              })),
            )
          : throwError(() => error),
      ),
    );
  }

  /** Состав, статус и KPI блоков дашборда из `GET /dashboards`, обновляется вместе с ним. */
  blockRegistry(dashboard: DashboardKey): Observable<ReadonlyMap<string, BlockSummary>> {
    this.freshness.refreshIfStale();

    let registry = this.registries.get(dashboard);

    if (registry === undefined) {
      registry = this.dashboards().pipe(
        map((dashboards) => {
          const blocks = dashboards.find((item) => item.key === dashboard)?.blocks ?? [];

          return new Map(blocks.map((summary) => [summary.key, summary] as const));
        }),
        catchError((error: unknown) => {
          this.registries.delete(dashboard);

          return throwError(() => error);
        }),
        shareReplay(1),
      );
      this.registries.set(dashboard, registry);
    }

    return registry;
  }

  filters(
    dashboard: DashboardKey,
    query: BlockQuery,
  ): Observable<ApiEnvelope<readonly DashboardFilterValues[]>> {
    return this.http
      .get<ApiEnvelope<DashboardFilterValues[]>>(`${this.base}/dashboards/${dashboard}/filters`, {
        ...NO_STORE,
        params: toParams(query),
      })
      .pipe(
        map((envelope) => ({
          ...envelope,
          data: (envelope.data ?? []).map((filter) => ({
            ...filter,
            values: filter.values.map((value) => ({ ...value, rows: toNumber(value.rows) ?? 0 })),
          })),
        })),
      );
  }

  /**
   * Сервер сужает значения фильтра и его собственным выбором — выбрав одно, другое уже не добавить.
   * Для каждого выбранного фильтра его значения берутся из запроса без него самого.
   */
  facetedFilters(
    dashboard: DashboardKey,
    query: BlockQuery,
  ): Observable<ApiEnvelope<readonly DashboardFilterValues[]>> {
    const selected = Object.keys(query.filters ?? {}).filter(
      (key) => (query.filters?.[key]?.length ?? 0) > 0,
    );

    if (selected.length === 0) {
      return this.filters(dashboard, query);
    }

    const without = selected.map((key) => {
      const { [key]: _own, ...rest } = query.filters ?? {};

      return this.filters(dashboard, { ...query, filters: rest }).pipe(
        map((envelope) => [key, envelope.data.find((filter) => filter.key === key)] as const),
      );
    });

    return forkJoin([this.filters(dashboard, query), ...without]).pipe(
      map(([base, ...own]) => {
        const replaced = new Map(own);

        return {
          ...base,
          data: base.data.map((filter) => replaced.get(filter.key) ?? filter),
        };
      }),
    );
  }

  details(dashboard: DashboardKey, query: BlockQuery): Observable<ApiEnvelope<DetailsPage>> {
    return this.http
      .get<ApiEnvelope<DetailsPage>>(`${this.base}/dashboards/${dashboard}/details`, {
        ...NO_STORE,
        params: toParams(query),
      })
      .pipe(
        map((envelope) => ({
          ...envelope,
          data: {
            ...envelope.data,
            total: toNumber(envelope.data.total) ?? 0,
            page: toNumber(envelope.data.page) ?? 0,
            page_size: toNumber(envelope.data.page_size) ?? 0,
          },
        })),
      );
  }

  /** Книга xlsx; ссылкой не открыть — нужен токен в заголовке. */
  exportBlock(dashboard: DashboardKey, block: string, query: BlockQuery): Observable<Blob> {
    return this.http.get(`${this.base}/dashboards/${dashboard}/export/${block}`, {
      ...NO_STORE,
      params: toParams(query),
      responseType: 'blob',
    });
  }
}

export function toParams(query: BlockQuery): HttpParams {
  let params = new HttpParams();
  const { filters, ...scalars } = query;

  for (const [key, value] of Object.entries(scalars)) {
    if (value !== undefined && value !== null) {
      params = params.set(key, String(value));
    }
  }

  for (const [key, values] of Object.entries(filters ?? {})) {
    for (const value of values) {
      params = params.append(key, value);
    }
  }

  return params;
}

/** Окно сравнения ушло раньше начала данных — API отвечает 400 с параметром date_from. */
export function isCalendarShift(error: unknown): boolean {
  return (
    error instanceof HttpErrorResponse &&
    error.status === 400 &&
    (error.error as { parameter?: string } | null)?.parameter === 'date_from'
  );
}

/** int64 и decimal в контракте объявлены как `integer | string`. */
export function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const number = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(number) ? number : null;
}

function normalizeKpiCard(card: KpiCard): KpiCard {
  return {
    ...card,
    value: toNumber(card.value),
    numerator: toNumber(card.numerator),
    denominator: toNumber(card.denominator),
    target: toNumber(card.target),
    deviation: toNumber(card.deviation),
    trend_vs_previous: toNumber(card.trend_vs_previous),
    traffic_light:
      card.traffic_light === 'green' ||
      card.traffic_light === 'yellow' ||
      card.traffic_light === 'red'
        ? card.traffic_light
        : null,
  };
}
